// PWA install (licensed).
import { $, meta } from './core.ts';
import { toast } from './toast.ts';
import { installState } from './install-state.ts';

/** The browser's install prompt event (Chromium; not in the DOM typings). */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// ---------------- PWA (licensed: the layout writes <meta name="ds-pwa"> only then) ----------------
export function initPwa(): void {
  if (!meta('ds-pwa') || !('serviceWorker' in navigator)) return;
  navigator.serviceWorker
    .register('/sw.js', { scope: '/' })
    .then(reg => {
      reg.addEventListener('updatefound', () => {
        const w = reg.installing;
        w?.addEventListener('statechange', () => {
          if (w.state === 'installed' && navigator.serviceWorker.controller)
            toast('نسخه‌ی تازه آماده است.', {
              type: 'info',
              action: { label: 'به‌روزرسانی', run: () => location.reload() },
              timeout: 0,
            });
        });
      });
    })
    .catch(() => {});
  let deferred: BeforeInstallPromptEvent | null = null;
  let installed = false;

  // T-184: the «install the app» dialog (account menu): its state follows the browser — the install button when the
  // browser offers its prompt, the Share → Add to Home Screen steps on iPhone/iPad, the address-bar steps elsewhere, and
  // «installed» inside the installed app
  const refresh = (): void => {
    const box = $('[data-ds-install-box]');
    if (!box) return;
    const nav = navigator as Navigator & { standalone?: boolean };
    box.dataset.state = installState({
      standalone: installed || matchMedia('(display-mode: standalone)').matches || nav.standalone === true,
      canPrompt: deferred !== null,
      userAgent: navigator.userAgent,
      touchPoints: navigator.maxTouchPoints,
    });
  };
  refresh();
  document.addEventListener('click', e => {
    const target = e.target as Element;
    if (target.closest('[data-ds-dialog-open="ds-install"]')) refresh();
    if (!target.closest('[data-ds-install-now]') || !deferred) return;
    const prompt = deferred;
    deferred = null;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- the answer is read from userChoice below
    prompt.prompt();
    void prompt.userChoice.then(choice => {
      if (choice.outcome === 'accepted') installed = true;
      refresh();
    });
  });
  addEventListener('appinstalled', () => {
    installed = true;
    deferred = null;
    refresh();
  });

  addEventListener('beforeinstallprompt', e => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    refresh();
    const slot = $('[data-ds-install]');
    if (!slot) return;
    try {
      if (localStorage.getItem('ds-install-dismissed')) return;
    } catch {}
    slot.hidden = false;
    slot.querySelector('[data-install]')?.addEventListener('click', async () => {
      slot.hidden = true;
      if (!deferred) return;
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- the answer is read from userChoice below
      deferred.prompt();
      await deferred.userChoice;
      deferred = null;
    });
    slot.querySelector('[data-dismiss]')?.addEventListener('click', () => {
      slot.hidden = true;
      try {
        localStorage.setItem('ds-install-dismissed', '1');
      } catch {}
    });
  });
}
